import {IconButton} from '@wandb/components';
import {RunState} from '@wandb/weave/components/RunState';
import React, {useEffect, useState} from 'react';

import {navigateTo} from '../../util/history';
import {useIsTextOverflowing} from '../common/TruncateText/TruncateTextTooltip';
import {SemanticLoader as Loader} from '../utility/InstrumentedLoader';
import {useUserEnhancedQueueItems} from './hooks';
import {Queue} from './Queues/queueUtil';
import * as S from './RunQueueItemsPopoverContent.styles';
import {formatRelativeTime, RunQueuePopoverItem} from './runQueuePopoverUtil';

interface RunQueueItemsPopoverContentProps {
  queue: Queue;
  refetchQueue: () => void;
}

const ITEMS_PER_PAGE = 5;

const RelativeTimestamp: React.FC<{createdAt: string}> = ({createdAt}) => {
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  return <span>{formatRelativeTime(createdAt)}</span>;
};

const MiddleTruncatedText: React.FC<{text: string}> = ({text}) => {
  const {isTextOverflowing, parentDomRef} = useIsTextOverflowing({text});

  if (!isTextOverflowing) {
    return <span>{text}</span>;
  }

  // middle truncation this is based on a column max-width of
  // 200px and the Source Sans Pro font @ 15px.
  const maxLength = 25;
  if (text.length <= maxLength) {
    return <span>{text}</span>;
  }

  const ellipsis = '...';
  const availableChars = maxLength - ellipsis.length;
  const frontChars = Math.ceil(availableChars / 2);
  const backChars = Math.floor(availableChars / 2);

  const frontText = text.slice(0, frontChars);
  const backText = text.slice(-backChars);

  return (
    <div ref={parentDomRef} className="flex w-full min-w-0">
      <span className="shrink truncate">{frontText}</span>
      <span className="shrink-0">{ellipsis}</span>
      <span className="shrink truncate">{backText}</span>
    </div>
  );
};

export const RunQueueItemsPopoverContent: React.FC<
  RunQueueItemsPopoverContentProps
> = ({queue, refetchQueue}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const {items: userRunQueueItems, loading} = useUserEnhancedQueueItems(queue);

  // refetch for fresh data
  useEffect(() => {
    refetchQueue();
  }, [refetchQueue]);

  const totalItems = userRunQueueItems.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItems);

  const currentItems = userRunQueueItems.slice(startIndex, endIndex);

  const getRunUrl = (item: RunQueuePopoverItem) => {
    const runName = item.runName || item.associatedRunId;
    if (runName && item.state) {
      return `/${item.entityName}/${item.projectName}/runs/${runName}`;
    }
    return null;
  };

  const handleViewInQueue = (itemIndex: number) => {
    const item = currentItems[itemIndex];
    if (item) {
      navigateTo({
        location: {
          pathname: `/${queue.entityName}/launch/${queue.id}`,
          search: `?tab=runs&itemId=${item.id}`,
        },
      });
    }
  };

  return (
    <S.PopoverContainer>
      <S.Header>
        <S.HeaderTitle>In progress runs</S.HeaderTitle>
      </S.Header>

      {loading ? (
        <S.LoadingContainer>
          <Loader active name="tab-runs-loader" />
        </S.LoadingContainer>
      ) : totalItems === 0 ? (
        <S.EmptyStateContainer>No in progress runs</S.EmptyStateContainer>
      ) : (
        <>
          <S.TableContainer>
            <S.TableContent>
              {Array.from({length: ITEMS_PER_PAGE}, (_, index) => {
                const item = currentItems[index];

                if (!item) {
                  // fill with empty row for consistent height
                  return (
                    <S.TableRow key={`empty-${index}`}>
                      <S.TimeColumn />
                      <S.RunColumn />
                      <S.ProjectOrQueueColumn />
                      <S.StatusColumn />
                    </S.TableRow>
                  );
                }

                const runUrl = getRunUrl(item);
                return (
                  <S.TableRow key={item.id}>
                    <S.TimeColumn>
                      <RelativeTimestamp createdAt={item.createdAt} />
                    </S.TimeColumn>

                    <S.RunColumn>
                      {runUrl && item.type === 'RUN' ? (
                        <S.RunLink to={runUrl}>
                          <MiddleTruncatedText
                            text={
                              item.runDisplayName ||
                              item.runName ||
                              item.associatedRunId ||
                              'Pending run'
                            }
                          />
                        </S.RunLink>
                      ) : (
                        <span>
                          <MiddleTruncatedText
                            text={
                              item.runDisplayName ||
                              item.runName ||
                              item.associatedRunId ||
                              'Pending run'
                            }
                          />
                        </span>
                      )}
                    </S.RunColumn>
                    <S.ProjectOrQueueColumn>
                      {item.type === 'RUN_QUEUE_ITEM' ? (
                        <S.ClickableText
                          onClick={() =>
                            navigateTo({
                              location: {
                                pathname: `/${queue.entityName}/launch/${queue.id}`,
                              },
                            })
                          }>
                          <MiddleTruncatedText text={queue.name} />
                        </S.ClickableText>
                      ) : (
                        <S.ClickableText
                          onClick={() =>
                            navigateTo({
                              location: {
                                pathname: `/${item.entityName}/${item.projectName}`,
                              },
                            })
                          }>
                          <MiddleTruncatedText text={item.projectName} />
                        </S.ClickableText>
                      )}
                    </S.ProjectOrQueueColumn>
                    <S.StatusColumn>
                      <RunState value={item.state} />
                    </S.StatusColumn>
                  </S.TableRow>
                );
              })}
            </S.TableContent>

            <S.PaginationContainer>
              <S.PaginationText>
                {totalItems > 0
                  ? `${startIndex + 1}-${endIndex} of ${totalItems}`
                  : '0 of 0'}
              </S.PaginationText>

              <div style={{display: 'flex', gap: '4px'}}>
                <IconButton
                  icon="chevron-back"
                  variant="ghost"
                  aria-label="Previous page"
                  isDisabled={currentPage === 1}
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                />

                <IconButton
                  icon="chevron-next"
                  variant="ghost"
                  aria-label="Next page"
                  isDisabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                />
              </div>
            </S.PaginationContainer>
          </S.TableContainer>
          {/* todo(dom): implement this in a follow-up */}
          {/* <S.CheckboxContainer>
            <input
              type="checkbox"
              id="auto-open"
              checked={autoOpen}
              onChange={e => setAutoOpen(e.target.checked)}
            />
            <label htmlFor="auto-open">
              Do not open automatically after launch
            </label>
          </S.CheckboxContainer> */}
        </>
      )}
    </S.PopoverContainer>
  );
};
